import React, { useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { Button, Modal, Tooltip, Space } from '@bedrock/components';
import { $sType } from '@mpc/common';
import { ButtonProps } from '@bedrock/components/lib/Button';
import CooperationPropertyForm from '../CooperationPropertyForm';
import styles from './index.less';
import { DemandCooperatePropertyValueDto,
} from '#services/api';

const cx = classNames.bind(styles);

interface Props {
  value?: DemandCooperatePropertyValueDto[];
  maxRows?: number;
  index_id?: number;
  showValuesTitle?: boolean;
  moreButtonProps?: ButtonProps;
  ellipsisValue?: boolean;
  showMoreButtonPosition?: 'bottom' | 'top' | 'titleAfter' | 'none';
}

const renderStandardValue = ( standardValue?: DemandCooperatePropertyValueDto) => {
  const value = Array.isArray(standardValue?.value) ? standardValue?.value : [standardValue?.value];
  const options = standardValue?.standard_schema?.standards?.filter((si) => {
    return value.includes(si.code);
  });
  return options?.map((opt) => opt.name).join('、');
};

const renderPropertyValue = (property) => {
  const value = property?.value;
  if (!Array.isArray(value)) {
    return '--';
  }
  if (!value.length) {
    return '--';
  }

  if (property?.property_schema?.property_type === 'date_range') {
    return value.join(' ~ ')
  }

  return value.join('、');
};

export const renderCooperationPropertyValue = (property?: DemandCooperatePropertyValueDto) => {
  return property.type === 1 ? renderStandardValue(property) : renderPropertyValue(property);
}

export const renderCooperationPropertyLabel = (property?: DemandCooperatePropertyValueDto) => {
  return property?.property_schema?.property_name ?? property?.standard_schema?.label;
}

const CooperationDetails: React.FC<Props> = ({
  value,
  maxRows = 6,
  index_id,
  showValuesTitle,
  moreButtonProps,
  ellipsisValue,
  showMoreButtonPosition = 'bottom',
}) => {
  const [visible, setVisible] = useState(false);

  const sliceConfig = useMemo(() => {
    if (value?.length >= maxRows) {
      return {
        sliceRows: maxRows - 1,
        showMore: true,
      };
    }
    return {
      sliceRows: value?.length,
      showMore: false,
    };
  }, [value, maxRows]);

  const showMoreButton = useMemo(() => {
    if (showMoreButtonPosition === 'none') {
      return null;
    }
    return (
      !!value?.length && (
        <Button
          type="link"
          {...moreButtonProps}
          onClick={() => setVisible(true)}
        >
          查看更多...
        </Button>
      )
    );
  }, [showMoreButtonPosition, moreButtonProps, value]);

  return (
    <>
      <div className={cx('container')}>
        <div>
          {showValuesTitle ? (
            <Space className={cx('values-title')} size={1} direction="vertical">
              {`合作内容${index_id}`}
              {showMoreButtonPosition === 'titleAfter' && showMoreButton}
            </Space>
          ) : null}
          {showMoreButtonPosition === 'top' && showMoreButton}
          {value?.slice(0, sliceConfig.sliceRows)?.map((v, index) => {
            const valueRender = renderCooperationPropertyValue(v);

            return (
              <div
                className={cx('row', { 'row-ellipsis': ellipsisValue })}
                key={index}
              >
                <span className={cx('label')}>
                  {renderCooperationPropertyLabel(v)}
                </span>
                <Tooltip title={ellipsisValue ? valueRender : null}>
                  <span className={cx('value')}>{valueRender}</span>
                </Tooltip>
              </div>
            );
          })}
          {showMoreButtonPosition === 'bottom' && showMoreButton}
        </div>
      </div>
      <Modal
        visible={visible}
        onCancel={() => setVisible(false)}
        title={`合作内容${index_id}`}
        cancelButton={false}
        okButton={false}
      >
        <CooperationPropertyForm value={value} view />
      </Modal>
    </>
  );
};

export default CooperationDetails;
